/**
 * Stock ledger API — store endpoints re-exported from store management module.
 * Other pages import getStores from here for backward compatibility.
 */
import client from './inventory-request';
import { withStoreCreateBody } from '../hooks/useStoreScope';

export {
  getStores,
  getStore,
  createStore,
  updateStore,
  deleteStore,
  getStoreTree,
  getStoreBins,
  createStoreBin,
  updateStoreBin,
  deleteStoreBin,
  getAllowedTransactions,
  upsertAllowedTransactions,
  canStorePerformTxn,
  getStoreReorder,
  updateStoreReorder,
} from './stores';

// Stock Balances & Availability
// API: GET /inventory/stock/balances/
export const getStockBalances    = (p) => client.get('/inventory/stock/balances/', p);
// API: GET /inventory/stock/store-catalogue/
export const getStoreCatalogue   = (p) => client.get('/inventory/stock/store-catalogue/', p);
// API: GET /inventory/stock/availability/
export const checkAvailability   = (p) => client.get('/inventory/stock/availability/', p);
// API: POST /inventory/stock/fefo-pick/
export const fefoPickPlan        = (body) => client.get('/inventory/stock/fefo-pick/', body);
export const getFefo             = fefoPickPlan;
// API: GET /inventory/stock/{storeId}/{itemId}/
export const getStockByStoreItem = (storeId, itemId) => client.get(`/inventory/stock/${storeId}/${itemId}/`);

/** Normalize stock/balance payloads to a single available quantity number (or null). */
export function readAvailableQuantity(payload) {
  if (payload == null) return null;
  const row = Array.isArray(payload)
    ? payload[0]
    : (payload.items?.[0] ?? payload.data ?? payload);
  if (row == null || typeof row !== 'object') return null;
  const n = Number(
    row.available_quantity
      ?? row.total_available_quantity
      ?? row.on_hand_quantity
      ?? row.quantity
      ?? row.available,
  );
  return Number.isFinite(n) ? n : null;
}

/** True/false when the availability payload clearly says whether qty can be fulfilled; else null. */
export function readCanFulfill(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const row = Array.isArray(payload)
    ? payload[0]
    : (payload.items?.[0] ?? payload.data ?? payload);
  if (row == null || typeof row !== 'object') return null;
  if (typeof row.can_fulfill === 'boolean') return row.can_fulfill;
  if (typeof row.is_available === 'boolean') return row.is_available;
  const total = Number(row.total_available_quantity ?? row.available_quantity);
  const requested = Number(row.requested_quantity);
  if (Number.isFinite(total) && Number.isFinite(requested)) return total >= requested;
  return null;
}

// Stock Transactions
// API: POST /inventory/stock/receipt/
export const receiptStock = (body) => client.post('/inventory/stock/receipt/', body);
// API: POST /inventory/stock/issue/
export const issueStock   = (body) => client.post('/inventory/stock/issue/', body);

// Reservations
// API: GET /inventory/stock/reservations/
export const getReservations   = (p) => client.get('/inventory/stock/reservations/', p);
// API: POST /inventory/stock/reservations/
export const createReservation = (body) => client.post('/inventory/stock/reservations/', body);
// API: PATCH /inventory/stock/reservations/{id}/
export const updateReservation = (id, body) => client.patch(`/inventory/stock/reservations/${id}/`, body);

// Ledger
// API: GET /inventory/stock/ledger/
export const getLedger = (p) => client.get('/inventory/stock/ledger/', p);

// Opening Stock — one-time starting balance per item/store (batch + expiry + value).
// tenant_id/hospital_id are resolved server-side from ORG-CODE; created_by is injected
// from the session by withStoreCreateBody, matching every other stock write.
// API: GET /inventory/stock/opening-stock/
export const getOpeningStocks   = (p)    => client.get('/inventory/stock/opening-stock/', p);
// API: GET /inventory/stock/opening-stock/{id}/
export const getOpeningStock    = (id)   => client.get(`/inventory/stock/opening-stock/${id}/`);
// API: POST /inventory/stock/opening-stock/
export const createOpeningStock = (body) => client.post('/inventory/stock/opening-stock/', withStoreCreateBody(body));

// Transfers
// API: GET /inventory/stock/transfers/
export const getTransfers   = (p) => client.get('/inventory/stock/transfers/', p);
// API: POST /inventory/stock/transfers/
export const createTransfer = (body) => client.post('/inventory/stock/transfers/', body);
// API: PATCH /inventory/stock/transfers/{id}/
export const updateTransfer = (id, body) => client.patch(`/inventory/stock/transfers/${id}/`, body);
// A transfer is an inventory_transaction, so its detail comes from the generic
// transaction projection — there is no GET /transfers/{id}.
// API: GET /inventory/inventory-transactions/{id}/
export const getTransferDetail = (id) => client.get(`/inventory/inventory-transactions/${id}/`);

/** UUID for transfer reference_id — works on non-secure hosts (e.g. http://localhost.dev.com). */
export function newTransferReferenceId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function normalizePicks(rawPicks) {
  return (Array.isArray(rawPicks) ? rawPicks : []).map((p) => ({
    stock_balance_id: p.stock_balance_id,
    grn_item_id: p.grn_item_id,
    batch_number: p.batch_number || '',
    expiry_date: p.expiry_date || '',
    available_quantity: p.available_quantity,
    quantity: p.pick_quantity ?? p.quantity ?? null,
    pick_quantity: p.pick_quantity ?? p.quantity ?? null,
  }));
}

/**
 * Normalize create-transfer API response (already unwrapped `data` from client).
 * Maps pick_quantity → quantity for UI tables. A multi-line transfer answers with
 * `lines[]`; a single-line one also mirrors that line onto the top level.
 */
export function normalizeTransferResponse(res, fallbackItemId) {
  const data = (res && typeof res === 'object' && !Array.isArray(res)) ? res : {};
  const rawLines = Array.isArray(data.lines) ? data.lines : [];
  return {
    item_id: data.item_id || fallbackItemId,
    transaction_id: data.transaction_id || data.id || null,
    transaction_number: data.transaction_number || null,
    total_transferred: data.total_transferred ?? null,
    status: data.status || null,
    picks: normalizePicks(data.picks),
    lines: rawLines.map((line) => ({
      item_id: line.item_id,
      uom_id: line.uom_id,
      requested_quantity: line.requested_quantity ?? null,
      total_transferred: line.total_transferred ?? null,
      picks: normalizePicks(line.picks),
    })),
  };
}

/**
 * Build POST /inventory/stock/transfers/ body. Every line of a transfer goes in one
 * request so the backend writes a single transaction — sending them one at a time
 * leaves earlier lines already moved when a later one fails.
 *
 * tenant_id/hospital_id are resolved server-side from ORG-CODE; created_by is
 * injected from the session by withStoreCreateBody.
 */
export function buildTransferBody({
  from_store_id,
  to_store_id,
  items,
  reference_id,
  remarks,
  expected_date,
  transfer_reason,
}) {
  const body = withStoreCreateBody({
    from_store_id,
    to_store_id,
    reference_type: 'TRANSFER',
    reference_id,
    items: (items || []).map((line) => ({
      item_id: line.item_id,
      uom_id: line.uom_id,
      quantity: Number(line.quantity),
    })),
  });
  if (remarks) body.remarks = remarks;
  if (expected_date) body.expected_date = expected_date;
  if (transfer_reason) body.transfer_reason = transfer_reason;
  return body;
}

// Adjustments
// API: GET /inventory/stock/adjustments/
export const getAdjustments   = (p) => client.get('/inventory/stock/adjustments/', p);
// API: POST /inventory/stock/adjustments/
export const createAdjustment = (body) => client.post('/inventory/stock/adjustments/', body);
// API: PATCH /inventory/stock/adjustments/{id}/
export const updateAdjustment = (id, body) => client.patch(`/inventory/stock/adjustments/${id}/`, body);
